import {
  Form,
  Grid,
  notification,
  Drawer,
  Input,
  Select,
  Space,
  Icons,
  Checkbox,
  Button,
} from '@pankod/refine-antd';
import { useState, useEffect } from 'react';
import { IAttribute } from 'services/template';
import { capitalize } from 'utilities/shared';

interface EditTemplateAttributeDrawerProps {
  attribute: IAttribute | null;
  visible: boolean;
  onClose: () => void;
  onSubmit: (values: any) => Promise<void>;
}

export const EditTemplateAttributeDrawer: React.FC<EditTemplateAttributeDrawerProps> = ({
  attribute,
  visible,
  onClose,
  onSubmit,
}) => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [form] = Form.useForm();
  const breakpoint = Grid.useBreakpoint();

  useEffect(() => {
    if (!attribute) return;

    form.setFieldsValue({
      ...attribute,
      type: capitalize(attribute.type),
    });
  }, [attribute, form]);

  const handleClose = () => {
    onClose();
    form.resetFields();
  };

  const onFinish = async (values: any) => {
    if (!attribute) return;

    setIsLoading(true);
    try {
      let data = { id: attribute.id, ...values };
      if (values.type === 'Text' || values.type === 'Number') {
        data = { ...data, allowCustomValue: true };
      }

      await onSubmit(data);
      handleClose();
    } catch (err: any) {
      console.error(err.message);
      notification.error({
        message: 'Failed',
        description: err.message,
      });
    }
    setIsLoading(false);
  };

  return (
    <Drawer
      title={attribute ? `Edit attribute ${capitalize(attribute.name)}` : 'Edit attribute'}
      placement="right"
      width={breakpoint.sm ? '450px' : '100%'}
      visible={visible}
      onClose={handleClose}
    >
      {attribute && (
        <Form layout="vertical" initialValues={{ isActive: true }} form={form} onFinish={onFinish}>
          <Form.Item
            label="Name"
            name="name"
            rules={[{ required: true, message: "attribute 'name' missing" }]}
          >
            <Input placeholder="Name" />
          </Form.Item>

          <Form.Item
            label="Type"
            name="type"
            rules={[{ required: true, message: "attribute 'type' missing" }]}
            initialValue={'Text'}
          >
            <Select style={{ width: '100%' }} disabled>
              <Select.Option key={0} value="Text">
                <Space align="center">
                  <Icons.FileTextOutlined /> Text
                </Space>
              </Select.Option>
              <Select.Option key={1} value="Float">
                <Space align="center">
                  <Icons.NumberOutlined /> Number
                </Space>
              </Select.Option>
              <Select.Option key={2} value="Boolean">
                <Space align="center">
                  <Icons.CheckCircleOutlined /> Boolean
                </Space>
              </Select.Option>
              <Select.Option key={3} value="Dropdown">
                <Space align="center">
                  <Icons.UnorderedListOutlined /> Dropdown
                </Space>
              </Select.Option>
            </Select>
          </Form.Item>

          <Form.Item label="Options" name="options" hidden={!(attribute.type === 'dropdown')}>
            <Select mode="tags" placeholder="options" style={{ width: '100%' }} />
          </Form.Item>

          <Space>
            <Form.Item name="required" valuePropName="checked">
              <Checkbox>Required</Checkbox>
            </Form.Item>

            <Form.Item
              name="allowCustomValue"
              valuePropName="checked"
              hidden={!(attribute.type === 'dropdown')}
            >
              <Checkbox>Custom value</Checkbox>
            </Form.Item>
          </Space>

          <Form.Item wrapperCol={{ span: 4, offset: 17 }}>
            <Button type="primary" htmlType="submit" disabled={isLoading}>
              Save changes
            </Button>
          </Form.Item>
        </Form>
      )}
    </Drawer>
  );
};
